import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { createTranslator } from '../i18n';
import { Thermometer, Palmtree, HelpCircle, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AttendanceSelfService() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null); // 'invalid' | 'expired' | 'generic'
  const [lang, setLang] = useState('is'); // session-only, breytir EKKI preferred_language
  const [step, setStep] = useState('choose'); // choose | sick | vacation | other | forgot | done
  const [doneKey, setDoneKey] = useState('');
  const [multiDay, setMultiDay] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [explanation, setExplanation] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const t = createTranslator(lang);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/attendance/self-service/${token}`);
        setInfo(res.data);
        setLang(res.data.language === 'en' ? 'en' : 'is');
        if (res.data.response_status && res.data.response_status !== 'pending') {
          setStep('done');
          setDoneKey('attendance_missing_already_answered');
        }
      } catch (e) {
        if (e.response?.status === 410) setError('expired');
        else if (e.response?.status === 404) setError('invalid');
        else setError('generic');
      }
    };
    load();
  }, [token]);

  const respond = async (action, extra = {}, confirmationKey) => {
    setSubmitting(true);
    setSubmitError('');
    try {
      await axios.post(`${API}/attendance/self-service/${token}/respond`, { action, ...extra });
      setDoneKey(confirmationKey);
      setStep('done');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === 'already_answered') {
        setDoneKey('attendance_missing_already_answered');
        setStep('done');
      } else if (detail === 'expired_token') {
        setError('expired');
      } else {
        setSubmitError(t('attendance_missing_error_generic'));
      }
    }
    setSubmitting(false);
  };

  const LangSwitch = () => (
    <div className="flex justify-center gap-2 mb-6" data-testid="attendance-language-switch">
      <button
        onClick={() => setLang('is')}
        className={`px-3 py-1 rounded-full text-sm ${lang === 'is' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
        data-testid="attendance-lang-is"
      >
        Íslenska
      </button>
      <button
        onClick={() => setLang('en')}
        className={`px-3 py-1 rounded-full text-sm ${lang === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
        data-testid="attendance-lang-en"
      >
        English
      </button>
    </div>
  );

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" data-testid="attendance-self-service-page">
        <LangSwitch />
        {children}
      </div>
    </div>
  );

  if (error) {
    return (
      <Shell>
        <div className="text-center space-y-3" data-testid="attendance-error-page">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
          <h1 className="text-xl font-bold">
            {error === 'expired' ? t('attendance_missing_expired_link') : t('attendance_missing_invalid_link')}
          </h1>
        </div>
      </Shell>
    );
  }

  if (!info) {
    return (
      <Shell>
        <p className="text-center text-gray-500">...</p>
      </Shell>
    );
  }

  if (step === 'done') {
    return (
      <Shell>
        <div className="text-center space-y-3" data-testid="attendance-confirmation">
          <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
          <p className="text-lg font-medium">{t(doneKey)}</p>
        </div>
      </Shell>
    );
  }

  const desc = t('attendance_missing_description').replace('{time}', info.shift_start);

  return (
    <Shell>
      <h1 className="text-xl font-bold text-center mb-2" data-testid="attendance-title">
        {t('attendance_missing_title')}
      </h1>
      <p className="text-sm text-gray-600 text-center mb-6" data-testid="attendance-description">
        {info.first_name ? `${info.first_name}: ` : ''}{desc}
      </p>

      {step === 'choose' && (
        <div className="space-y-3">
          <Button className="w-full h-12 justify-start bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
            variant="outline" onClick={() => setStep('sick')} data-testid="attendance-option-sick">
            <Thermometer className="mr-3 h-5 w-5" /> {t('attendance_missing_sick')}
          </Button>
          <Button className="w-full h-12 justify-start bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
            variant="outline" onClick={() => setStep('vacation')} data-testid="attendance-option-vacation">
            <Palmtree className="mr-3 h-5 w-5" /> {t('attendance_missing_vacation')}
          </Button>
          <Button className="w-full h-12 justify-start bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
            variant="outline" onClick={() => setStep('other')} data-testid="attendance-option-other">
            <HelpCircle className="mr-3 h-5 w-5" /> {t('attendance_missing_other')}
          </Button>
          <Button className="w-full h-12 justify-start bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
            variant="outline" onClick={() => setStep('forgot')} data-testid="attendance-option-forgot">
            <Clock className="mr-3 h-5 w-5" /> {t('attendance_missing_forgot_clock_in')}
          </Button>
        </div>
      )}

      {step === 'sick' && (
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={multiDay} onChange={(e) => setMultiDay(e.target.checked)}
              data-testid="attendance-sick-multiday" />
            {t('attendance_missing_sick_multi_day')}
          </label>
          {multiDay && (
            <div>
              <label className="text-sm text-gray-600">{t('attendance_missing_sick_end_date')}</label>
              <Input type="date" value={endDate} min={info.shift_date}
                onChange={(e) => setEndDate(e.target.value)} data-testid="attendance-sick-end-date" />
              <p className="text-xs text-gray-400 mt-1">{t('attendance_missing_sick_end_date_hint')}</p>
            </div>
          )}
          {submitError && <p className="text-sm text-red-600">{submitError}</p>}
          <Button className="w-full" disabled={submitting}
            onClick={() => respond('sick', multiDay && endDate ? { end_date: endDate } : {}, 'attendance_missing_confirmation_sick')}
            data-testid="attendance-sick-submit">
            {t('attendance_missing_submit')}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => setStep('choose')}>{t('attendance_missing_back')}</Button>
        </div>
      )}

      {step === 'vacation' && (
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-600">{t('attendance_missing_sick_end_date')}</label>
            <Input type="date" value={endDate} min={info.shift_date}
              onChange={(e) => setEndDate(e.target.value)} data-testid="attendance-vacation-end-date" />
          </div>
          {submitError && <p className="text-sm text-red-600">{submitError}</p>}
          <Button className="w-full" disabled={submitting}
            onClick={() => respond('vacation', endDate ? { end_date: endDate } : {}, 'attendance_missing_confirmation_vacation')}
            data-testid="attendance-vacation-submit">
            {t('attendance_missing_submit')}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => setStep('choose')}>{t('attendance_missing_back')}</Button>
        </div>
      )}

      {step === 'other' && (
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-600">{t('attendance_missing_explanation_label')}</label>
            <Textarea value={explanation} onChange={(e) => setExplanation(e.target.value)}
              placeholder={t('attendance_missing_explanation_placeholder')} data-testid="attendance-other-explanation" />
          </div>
          {submitError && <p className="text-sm text-red-600">{submitError}</p>}
          <Button className="w-full" disabled={submitting || !explanation.trim()}
            onClick={() => respond('other', { explanation: explanation.trim() }, 'attendance_missing_confirmation_other')}
            data-testid="attendance-other-submit">
            {t('attendance_missing_submit')}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => setStep('choose')}>{t('attendance_missing_back')}</Button>
        </div>
      )}

      {step === 'forgot' && (
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-600">{t('attendance_missing_arrival_time_label')}</label>
            <Input type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)}
              data-testid="attendance-forgot-time" />
          </div>
          {submitError && <p className="text-sm text-red-600">{submitError}</p>}
          <Button className="w-full" disabled={submitting || !arrivalTime}
            onClick={() => respond('forgot_clock_in', { clock_in_time: arrivalTime }, 'attendance_missing_correction_requested')}
            data-testid="attendance-forgot-submit">
            {t('attendance_missing_submit')}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => setStep('choose')}>{t('attendance_missing_back')}</Button>
        </div>
      )}
    </Shell>
  );
}
